import ImageOutlined from '@mui/icons-material/ImageOutlined';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import { Theme, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { MESSAGING_LABEL_FONT_WEIGHT } from '~pages/Messages/messagingTypography';

// The card's metrics come from the messaging design mockup, which sizes an attachment row
// smaller than the message text around it so a file reads as an enclosure, not a reply.
const TILE_SIZE_IN_PX = 30;
const TILE_RADIUS_IN_PX = 5;
const FILE_NAME_FONT_SIZE_IN_PX = 11.5;
const SECONDARY_FONT_SIZE_IN_PX = 10;

export const attachmentCardSx = (theme: Theme) => ({
  alignItems: 'center',
  backgroundColor: theme.semantic.surfaceRaised,
  border: `1px solid ${theme.semantic.borderStrong}`,
  borderRadius: 1.5,
  display: 'flex',
  gap: 1.125,
  minWidth: 0,
  px: 1.125,
  py: 0.875,
  textAlign: 'left',
  textDecoration: 'none',
  width: '100%',
});

type AttachmentCardProps = {
  fileName: string;
  /** Absent while a send is still in flight, and on every file that is not an image. */
  imageUrl?: string;
  isImage: boolean;
  isPending?: boolean;
  secondaryText?: string;
};

/** The tile, name and size shared by a sent attachment and one still on its way up. */
export default function AttachmentCard({
  fileName,
  imageUrl,
  isImage,
  isPending = false,
  secondaryText,
}: AttachmentCardProps) {
  const theme = useTheme();

  return (
    <>
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: theme.semantic.statusInfoBg,
          borderRadius: `${TILE_RADIUS_IN_PX}px`,
          color: isPending ? theme.semantic.textSecondary : theme.semantic.statusInfo,
          display: 'flex',
          flex: `0 0 ${TILE_SIZE_IN_PX}px`,
          height: `${TILE_SIZE_IN_PX}px`,
          justifyContent: 'center',
          overflow: 'hidden',
          width: `${TILE_SIZE_IN_PX}px`,
        }}
      >
        {imageUrl ? (
          <Box
            alt={fileName}
            component="img"
            src={imageUrl}
            sx={{ height: '100%', objectFit: 'cover', width: '100%' }}
          />
        ) : (
          <Box
            component={isImage ? ImageOutlined : InsertDriveFileOutlined}
            sx={{ fontSize: 16 }}
          />
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          noWrap
          sx={{
            color: isPending ? theme.semantic.textSecondary : theme.semantic.textPrimary,
            fontSize: `${FILE_NAME_FONT_SIZE_IN_PX}px`,
            fontWeight: MESSAGING_LABEL_FONT_WEIGHT,
            lineHeight: 1.2,
          }}
          title={fileName}
        >
          {fileName}
        </Typography>

        {secondaryText && (
          <Typography
            sx={{
              color: theme.semantic.textSecondary,
              fontSize: `${SECONDARY_FONT_SIZE_IN_PX}px`,
            }}
          >
            {secondaryText}
          </Typography>
        )}
      </Box>
    </>
  );
}
